import type {
  AccountTreeNode,
  DistributionGroup,
  GroupsPayload
} from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { GroupSubpanel } from "./GroupsSubpanel";
import { AccountsPanel } from "./AccountsPanel";
import "./DistributionListPanel.scss";
import { useEffect, useState } from "react";
import { PrefFilterPanel } from "./PrefFilterPanel";
import type {
  FilterData,
  FilterDataPayload
} from "@dealer-center/ddc-pages-incentives-manager-lambda";
import type { MultiSelectEntry } from "./MultiSelect";

export interface DisplayAccountTreeNode extends AccountTreeNode {
  disabled?: boolean;
  state: string;
  franchises: string[];
}
export interface FilterInterface {
  franchises: MultiSelectEntry[];
  states: MultiSelectEntry[];
}

export interface DistributionListPanelProps {
  groups: GroupsPayload;
  accountTree?: DisplayAccountTreeNode;
  filterData?: FilterDataPayload;
  onGroupsChanged?: (group: GroupsPayload) => void;
}

const getGroupById = (
  id: string,
  groups: GroupsPayload
): DistributionGroup | undefined => {
  const group = Object.values(groups).find(group => group.id === id);
  return group;
};

export function disableTreeNodes(
  node: DisplayAccountTreeNode,
  filters: FilterInterface
): DisplayAccountTreeNode {
  const disableNode = (node: DisplayAccountTreeNode) => {
    const isStateMatch = filters.states.some(
      state => state.value === node.state && state.active
    );
    const isFranchiseMatch = filters.franchises.some(
      franchise => franchise.active && node.franchises.includes(franchise.value)
    );
    node.disabled = !(isStateMatch && isFranchiseMatch);
    if (!node.disabled) {
      delete node.disabled;
    }
    node.children.forEach(child => {
      disableNode(child as DisplayAccountTreeNode);
    });
  };

  const newNode = JSON.parse(JSON.stringify(node)) as DisplayAccountTreeNode;
  disableNode(newNode);
  return newNode;
}

export function addFiltersToTreeNodes(
  node: DisplayAccountTreeNode,
  filterData: FilterData
): DisplayAccountTreeNode {
  const addFiltersToNode = (node: DisplayAccountTreeNode) => {
    node.state = filterData.states[node.id] || "";
    node.franchises = filterData.franchises[node.id] || [];
    node.children.forEach(child => {
      addFiltersToNode(child as DisplayAccountTreeNode);
    });
  };

  const newNode = JSON.parse(JSON.stringify(node)) as DisplayAccountTreeNode;
  addFiltersToNode(newNode);
  return newNode;
}

export const DistributionListPanel = ({
  groups,
  accountTree,
  filterData,
  onGroupsChanged
}: DistributionListPanelProps) => {
  const [currentGroups, setCurrentGroups] = useState<GroupsPayload>(groups);
  const hasGroups = Object.keys(currentGroups || {}).length > 0;
  const [currentGroupId, setCurrentGroupId] = useState<string>(
    (hasGroups && Object.values(groups || {})[0]?.id) || ""
  );
  const [filters, setFilters] = useState<FilterInterface>({
    franchises: [],
    states: []
  });
  const [displayAccountTree, setDisplayAccountTree] =
    useState<DisplayAccountTreeNode | null>(null);

  const updateGroup = (
    updated: DistributionGroup,
    groups: GroupsPayload
  ): void => {
    const clonedGroups = JSON.parse(JSON.stringify(groups));
    Object.keys(clonedGroups).forEach(key => {
      if (clonedGroups[key].id === updated.id) {
        clonedGroups[key] = updated;
      }
    });
    setCurrentGroups(clonedGroups);
  };

  useEffect(() => {
    if (currentGroups && !getGroupById(currentGroupId, currentGroups)) {
      const firstGroup = Object.values(currentGroups)[0];
      if (firstGroup) {
        setCurrentGroupId(firstGroup.id);
      }
    }
    onGroupsChanged?.(currentGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroups]);

  useEffect(() => {
    if (filterData && filterData.options) {
      const franchises: MultiSelectEntry[] = filterData.options.franchises.map(
        franchise => ({ label: franchise, value: franchise, active: true })
      );
      const states: MultiSelectEntry[] = filterData.options.states.map(
        state => ({
          label: state,
          value: state,
          active: true
        })
      );
      setFilters({ franchises, states });
    }
  }, [filterData]);

  useEffect(() => {
    if (accountTree && filterData) {
      const newAccountTree = addFiltersToTreeNodes(
        accountTree as DisplayAccountTreeNode,
        filterData?.filterData || {}
      );
      setDisplayAccountTree(newAccountTree);
    }
  }, [accountTree, filterData]);

  useEffect(() => {
    if (filters && displayAccountTree) {
      const newAccountTree = disableTreeNodes(displayAccountTree, filters);
      setDisplayAccountTree(newAccountTree);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <div className="distribution-list-panel">
      {groups && (
        <GroupSubpanel
          groups={currentGroups}
          selectedGroup={currentGroupId}
          onGroupCreate={newGroups => {
            setCurrentGroups(newGroups);
            setCurrentGroupId(
              Object.values(newGroups)[newGroups.length - 1].id
            );
          }}
          onGroupDelete={updatedGroups => {
            setCurrentGroups(updatedGroups);
          }}
          onGroupSelect={group => setCurrentGroupId(group)}
        />
      )}

      {displayAccountTree && hasGroups && (
        <>
          <PrefFilterPanel filters={filters} setFilters={setFilters} />
          <AccountsPanel
            accountTree={displayAccountTree}
            group={getGroupById(currentGroupId, currentGroups)}
            onGroupChanged={updatedGroup => {
              updateGroup(updatedGroup, currentGroups);
            }}
          />
        </>
      )}
    </div>
  );
};
